import React, { createContext, useCallback, useContext, useLayoutEffect, useState, useSyncExternalStore } from 'react';

/**
 * ModalStackContext
 *
 * Tracks the depth of currently-open overlays (Modals, BottomSheets, HoverCards)
 * so each can pick a z-index that stacks correctly above anything beneath it.
 *
 * Without this, a Modal opened from inside a BottomSheet would render below
 * the sheet because both fight over a single hardcoded z-index tier.
 */

/**
 * The stack is the REAL list of currently open overlay slots, bottom to top; an overlay's depth is its position
 * among them (1 = bottom). (The previous design handed out a monotonic counter that only reset when ALL overlays
 * closed, so a lone drawer could land at depth 6 / z 130 and a later modal could end up below it.) Later
 * registrations are appended, so whatever opens after an overlay is strictly above it; closing one shifts the
 * ones above it down.
 *
 * It lives in an external store read with useSyncExternalStore and a per-slot selector: a consumer re-renders
 * only when ITS OWN depth changes, not on every open/close elsewhere (closed modals, the heavy drawer root).
 */
interface StackStore {
  register: (ids: readonly number[]) => void;
  unregister: (ids: readonly number[]) => void;
  subscribe: (listener: () => void) => () => void;
  depthOf: (id: number) => number;
  size: () => number;
}

const createStackStore = (): StackStore => {
  let stack: readonly number[] = [];
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((listener) => listener());
  return {
    register: (ids) => {
      stack = [...stack, ...ids.filter((id) => !stack.includes(id))];
      emit();
    },
    unregister: (ids) => {
      stack = stack.filter((entry) => !ids.includes(entry));
      emit();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    depthOf: (id) => stack.indexOf(id) + 1,
    size: () => stack.length,
  };
};

// The store object is stable for the provider's lifetime, so the context value never changes.
const ModalStackContext = createContext<StackStore | null>(null);

let nextOverlayId = 1;

export function ModalStackProvider({ children }: { children: React.ReactNode }) {
  const [store] = useState(createStackStore);
  return <ModalStackContext.Provider value={store}>{children}</ModalStackContext.Provider>;
}

const noopSubscribe = (): (() => void) => () => undefined;

/**
 * Registers `slots` consecutive stack slots while `visible` is true and returns the depth of the LAST slot
 * (1 = bottom of the stack, 0 = not registered yet / not visible). A drawer reserves 2 slots (its own tier plus a
 * nested tier), so it sits at 90/100 alone and anything opened later stacks above it.
 *
 * Pass the signal that reflects actual DOM presence (including an exit animation), so the depth stays stable.
 */
export function useModalStackSlots(visible: boolean, slots: number): number {
  const store = useContext(ModalStackContext);
  const [lastId, setLastId] = useState(0);

  // Layout effect: registration lands before first paint, so a modal never shows one frame at depth 0 behind a drawer.
  useLayoutEffect(() => {
    if (!store || !visible) return;
    const ids = Array.from({ length: slots }, () => nextOverlayId++);
    store.register(ids);
    setLastId(ids[ids.length - 1]);
    return () => {
      store.unregister(ids);
      setLastId(0);
    };
  }, [visible, slots, store]);

  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    () => (store && lastId !== 0 ? store.depthOf(lastId) : 0),
    () => 0
  );
}

/** One slot: see useModalStackSlots. */
export function useModalStackDepth(visible: boolean): number {
  return useModalStackSlots(visible, 1);
}

/**
 * Returns a stable `(depth) => boolean` that says, AT CALL TIME (read it from an event handler, not render),
 * whether the overlay at `depth` is the topmost open layer. Used so one Escape closes only the top layer.
 * Without a provider, or for depth <= 0 (not registered), it answers true so the caller keeps working.
 */
export function useIsTopmostLayerCheck(): (depth: number) => boolean {
  const store = useContext(ModalStackContext);
  return useCallback((depth: number) => !store || depth <= 0 || depth >= store.size(), [store]);
}

/** Deepest stack level that still gets its own z tier (content 130). */
const MAX_STACK_DEPTH = 5;

/**
 * Maps a stack depth to z-index values for backdrop and content.
 *
 *   depth 1 → 80 / 90    (z-modal-backdrop / z-modal-content)
 *   depth 2 → 90 / 100   (z-modal-nested tier)
 *   depth 3 → 100 / 110
 *   depth 4 → 110 / 120
 *   depth 5 → 120 / 130  (clamped: deeper levels share it, DOM order decides)
 *
 * The clamp keeps stack content below the fixed tiers: portal popovers/dropdowns (z-popover-top 140), toasts
 * (150) and tooltips (160) are always above any modal. `depth <= 0` falls back to depth 1 values so components
 * not wrapped in a provider still render sensibly.
 */
export function getStackZIndex(depth: number): { backdrop: number; content: number } {
  const effective = Math.min(MAX_STACK_DEPTH, Math.max(1, depth));
  return {
    backdrop: 80 + (effective - 1) * 10,
    content: 90 + (effective - 1) * 10,
  };
}
